import { computed, Injectable, Signal } from '@angular/core';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { Order } from '@seatix/shared-domain-order';

export interface StoredOrderData {
  order: Order;
  timestamp: number;
}

/**
 * Reactive order storage service using ngxtension's injectLocalStorage.
 *
 * This service provides real-time synchronization of recent order data across the application,
 * eliminating the need for manual polling and ensuring consistent state management.
 *
 * Key Features:
 * - Reactive signals for order data and expiration status
 * - Automatic cross-tab synchronization via localStorage events
 * - Built-in serialization/deserialization handling
 * - Type-safe storage operations
 *
 * Usage Example:
 * ```typescript
 * @Component({
 *   template: `
 *     @if (orderStorage.isExpired()) {
 *       <p>Session expired</p>
 *     } @else if (orderStorage.order(); as order) {
 *       <h1>Order {{ order.id }}</h1>
 *     }
 *   `
 * })
 * export class CheckoutSuccessComponent {
 *   protected readonly orderStorage = inject(OrderStorageService);
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class OrderStorageService {
  readonly #STORAGE_KEY = 'stx_recent_order';
  readonly #PAYMENT_METHOD_KEY = 'stx_last_payment_method';
  readonly #MAX_AGE_MINUTES = 15;

  /**
   * Reactive signal for stored order data with automatic localStorage synchronization.
   * Uses ngxtension's injectLocalStorage for seamless state management.
   */
  readonly #storedOrder = injectLocalStorage<StoredOrderData | null>(
    this.#STORAGE_KEY,
    {
      defaultValue: null,
    },
  );

  /**
   * Computed signal that extracts the order from stored data.
   * Returns null if no order is stored or data is malformed.
   */
  public readonly order: Signal<Order | null> = computed(
    () => this.#storedOrder()?.order ?? null,
  );

  /**
   * Computed signal that determines if the stored order has expired.
   * Returns true if no order exists or if it's older than MAX_AGE_MINUTES.
   * Automatically recalculates when stored data changes.
   */
  public readonly isExpired: Signal<boolean> = computed(() => {
    const storedData = this.#storedOrder();
    if (!storedData) {
      return true;
    }
    const maxAgeMs = this.#MAX_AGE_MINUTES * 60 * 1000;
    return Date.now() - storedData.timestamp > maxAgeMs;
  });

  /**
   * Computed signal that checks if a valid, non-expired order exists.
   * Useful for conditional rendering and navigation guards.
   */
  public readonly hasValidOrder: Signal<boolean> = computed(
    () => this.order() !== null && !this.isExpired(),
  );

  /**
   * Save an order to localStorage with timestamp.
   * Automatically triggers reactivity across all consumers.
   *
   * @param order - The order object to store
   */
  saveOrder(order: Order): void {
    const data: StoredOrderData = {
      order,
      timestamp: Date.now(),
    };
    this.#storedOrder.set(data);
  }

  /**
   * Clear the stored order data.
   * Automatically triggers reactivity across all consumers.
   */
  clearOrder(): void {
    this.#storedOrder.set(null);
  }

  /**
   * Check if stored order is expired based on custom age limit.
   *
   * @param maxAgeMinutes - Custom expiration time in minutes
   * @returns true if order is expired or doesn't exist
   */
  isOrderExpired(maxAgeMinutes: number): boolean {
    const storedData = this.#storedOrder();
    if (!storedData) return true;

    const maxAgeMs = maxAgeMinutes * 60 * 1000;
    return Date.now() - storedData.timestamp > maxAgeMs;
  }

  /**
   * Save the last successful payment method to localStorage.
   *
   * @param paymentMethod - The payment method that was used successfully ('card', 'oxxo', etc.)
   */
  saveLastPaymentMethod(paymentMethod: string): void {
    localStorage.setItem(this.#PAYMENT_METHOD_KEY, paymentMethod);
  }

  /**
   * Get the last successful payment method from localStorage.
   *
   * @returns The last payment method used, or null if none exists
   */
  getLastPaymentMethod(): string | null {
    return localStorage.getItem(this.#PAYMENT_METHOD_KEY);
  }

  /**
   * Clear the last successful payment method from localStorage.
   */
  clearLastPaymentMethod(): void {
    localStorage.removeItem(this.#PAYMENT_METHOD_KEY);
  }
}
